/**
 * Raises a bell notification when an office's PMS data goes stale or
 * offline, and clears it when sync recovers (BUILD-PLAN 2.5.6). Text names
 * the office only, never a patient.
 */
import type { OfficeFreshness } from '@velora/contracts/shell';
import { freshnessLevel, type FreshnessLevel } from '@velora/ui';
import { useEffect, useRef } from 'react';
import { notifications } from '../notifications/store.js';
import { useOfficeScope } from '../office/OfficeScope.js';
import { useFreshness } from './TopBarParts.js';

const FROM_SYNC_LEVEL: Record<NonNullable<OfficeFreshness['syncLevel']>, FreshnessLevel> = {
  ok: 'recent',
  warning: 'stale',
  error: 'offline',
  unknown: 'unknown',
};

/** "22 minutes": `times` x the connector interval plus 2 minutes, or the fixed default. */
function late(intervalSec: number | undefined, times: number, fallbackMin: number): string {
  const min = intervalSec ? Math.round((times * intervalSec + 120) / 60) : fallbackMin;
  return `${min} minutes`;
}

export function FreshnessMonitor() {
  const { data } = useFreshness();
  const { offices } = useOfficeScope();
  const last = useRef(new Map<string, string>());

  useEffect(() => {
    if (!data) return;
    const now = Date.parse(data.checkedAt);
    for (const row of data.offices) {
      if (row.pmsSystem === null) continue;
      const key = `freshness:${row.locationId}`;
      const name = offices.find((o) => o.id === row.locationId)?.name ?? 'An office';
      // PulseSync offices: the API's connector-interval rule (warning past
      // 2x the interval plus 2 minutes, error past 4x), the same as the
      // Home alert and the Sync status page; otherwise the fixed thresholds.
      const level =
        row.syncLevel !== undefined
          ? FROM_SYNC_LEVEL[row.syncLevel]
          : freshnessLevel(row.lastSyncedAt, Number.isFinite(now) ? now : Date.now());
      // Only on a change of level, so a refetch does not re-raise a read alert.
      if (last.current.get(key) === level) continue;
      last.current.set(key, level);
      if (level === 'offline') {
        notifications.notify({
          key,
          tone: 'danger',
          href: '/settings/connectors',
          title: `${name}: PMS data is not updating`,
          body: `The last successful sync was over ${late(row.intervalSec, 4, 30)} ago. Check the office connector.`,
        });
      } else if (level === 'stale') {
        notifications.notify({
          key,
          tone: 'warning',
          href: '/settings/connectors',
          title: `${name}: PMS data is delayed`,
          body: `The last successful sync was more than ${late(row.intervalSec, 2, 5)} ago.`,
        });
      } else if (level === 'live' || level === 'recent') {
        notifications.resolve(key);
      }
    }
  }, [data, offices]);

  return null;
}
